<!-- Auftragsbedarf: Material, das der Techniker vor Ort verbraucht (kein Versand). -->
<div class="bm-card bm-card-sm bm-card-border border-base-300">
  <div class="bm-card-body gap-3">
    <div class="flex flex-wrap items-center gap-2">
      <div class="bm-card-title flex-1 min-w-0">
        Auftragsbedarf
        @if (operationTechnicianName) {
          <span class="text-sm font-normal opacity-60">· {{ operationTechnicianName }}</span>
        }
      </div>
      @if (isCheckingAvailability()) {
        <span class="text-xs opacity-60">Verfügbarkeit wird geprüft …</span>
      }
      <!-- Bei Versand-Prioritaet gibt es hier nichts zu tun: kein Anlegen und
           auch nichts nachzurechnen. Der Hinweis darunter sagt, warum.
           Aktualisieren, weil die Prognose an Versandconfigs anderer Auftraege
           hängt — die können sich nebenbei ändern. -->
      @if (!isShippingPriority()) {
        <button
          class="bm-btn bm-btn-sm bm-btn-ghost"
          [disabled]="isCheckingAvailability()"
          title="Verfügbarkeit neu berechnen"
          (click)="refreshAvailability()"
        >
          ↻ Aktualisieren
        </button>
        <button class="bm-btn bm-btn-sm bm-btn-primary" (click)="openAdd()">+ Artikel</button>
      }
    </div>

    <!-- Sichtbar machen, woran es liegt: sonst wundert man sich, warum die
         Schaltflaechen fehlen, und sucht den Fehler an der falschen Stelle.
         Bereits angelegte Zeilen bleiben darunter sichtbar und löschbar. -->
    @if (isShippingPriority()) {
      <div class="bm-alert bm-alert-info bm-alert-soft">
        <span>
          Dieses Ticket hat eine <strong>Versand-Priorität</strong> (Materialversand). Hier wird
          kein Material vor Ort verbraucht.
        </span>
      </div>
    }

    @if (isLoading()) {
      <div class="text-sm opacity-60">Wird geladen …</div>
    } @else if (requirements().length === 0) {
      @if (!isShippingPriority()) {
        <div class="text-sm opacity-60">
          Noch kein Auftragsbedarf hinterlegt. Hier wird eingetragen, was der Techniker beim Auftrag
          verbaut — daraus entsteht kein Versand, es wird nur geprüft, ob er es rechtzeitig hat.
        </div>
      }
    } @else {
      <!-- Sammelhinweis: was fehlt zum Auftragstermin? -->
      @if (missingTotal() > 0) {
        <div class="bm-alert bm-alert-warning bm-alert-soft">
          <span class="flex-1">
            {{ missingEntries().length }} von {{ requirements().length }} Artikeln zum Auftragstermin
            voraussichtlich nicht verfügbar ({{ missingTotal() }} Stück fehlen).
          </span>
          <button class="bm-btn bm-btn-sm bm-btn-warning" [disabled]="isAdopting()" (click)="adoptMissing()">
            Fehlmengen als Versandartikel übernehmen
          </button>
        </div>
      } @else if (lateEntries().length > 0) {
        <div class="bm-alert bm-alert-info bm-alert-soft">
          <span>Material ist beauftragt, kommt laut Prognose aber erst nach dem Auftragstermin an.</span>
        </div>
      } @else if (availability()) {
        <div class="bm-alert bm-alert-success bm-alert-soft">
          <span>Alle Artikel zum Auftragstermin voraussichtlich verfügbar.</span>
        </div>
      }

      <table class="w-full text-sm border-collapse">
        <thead>
          <tr class="border-b border-base-300">
            <th class="text-left p-2 font-semibold">Artikel</th>
            <th class="text-right p-2 font-semibold w-20">Bedarf</th>
            <th class="text-left p-2 font-semibold w-80">Verfügbarkeit</th>
            <th class="p-2 w-32"></th>
          </tr>
        </thead>
        <tbody>
          @for (req of requirements(); track req.id) {
            @let entry = entryFor(req);
            <tr class="border-b border-base-200">
              <td class="p-2">{{ label(req) }}</td>
              <td class="p-2 text-right font-medium">{{ req.quantity }}</td>
              <td class="p-2">
                @if (!entry) {
                  <span class="text-xs opacity-50">—</span>
                } @else {
                  <div class="flex items-center gap-2">
                    <span
                      class="bm-badge bm-badge-xs w-fit"
                      [class.bm-badge-success]="entry.status === 'covered'"
                      [class.bm-badge-warning]="entry.status === 'late'"
                      [class.bm-badge-error]="entry.status === 'missing'"
                      [class.bm-badge-neutral]="entry.status === 'unknown'"
                    >
                      {{ statusLabel(entry) }}
                    </span>
                    @if (hasLedger(entry)) {
                      <button
                        class="bm-btn bm-btn-xs bm-btn-primary"
                        title="Zeigt, wie sich die Verfügbarkeit errechnet"
                        (click)="openLedger(entry)"
                      >
                        Berechnung
                      </button>
                    }
                  </div>
                }
              </td>
              <td class="p-2">
                <div class="flex justify-end gap-1">
                  @if (entry && entry.status === "missing") {
                    <button
                      class="bm-btn bm-btn-xs bm-btn-warning"
                      [disabled]="isAdopting()"
                      (click)="adoptMissing(entry)"
                    >
                      In Versand
                    </button>
                  }
                  <button class="bm-btn bm-btn-xs bm-btn-ghost" (click)="openEdit(req)">✎</button>
                  <button class="bm-btn bm-btn-xs bm-btn-ghost" (click)="deleteRequirement(req)">🗑</button>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    }
  </div>
</div>

<!-- Berechnungstabelle: je Artikelvorlage ein Kontoauszug vom heutigen Bestand
     bis zum Auftragstermin. -->
@if (showLedger()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (mousedown)="closeLedger()">
    <div
      class="bg-base-100 rounded-lg shadow-xl w-full max-w-5xl mx-4 p-5 flex flex-col gap-4 max-h-[85vh]"
      (mousedown)="$event.stopPropagation()"
    >
      <div class="flex items-center gap-2">
        <h3 class="font-bold text-base flex-1">Berechnung der Verfügbarkeit</h3>
        <button class="bm-btn bm-btn-sm bm-btn-ghost" (click)="closeLedger()">Schließen</button>
      </div>

      <!-- Ein Tab je Artikelvorlage; Freitextzeilen haben keinen Bestand und damit keinen Tab. -->
      <div role="tablist" class="bm-tabs bm-tabs-border">
        @for (tab of ledgerTabs(); track tab.operation_requirement_id) {
          <a
            role="tab"
            class="bm-tab"
            [class.bm-tab-active]="tab.article_template_id === activeLedgerEntry()?.article_template_id"
            (click)="ledgerTemplateId.set(tab.article_template_id ?? null)"
          >
            {{ tab.label }}
          </a>
        }
      </div>

      @if (activeLedgerEntry(); as active) {
        <div class="overflow-y-auto">
          <div class="text-sm mb-3">
            Bedarf <strong>{{ active.required }}</strong> zum Auftragstermin
            <strong>{{ formatDate(availability()?.operation_date) }}</strong> ·
            voraussichtlich verfügbar <strong>{{ active.available ?? 0 }}</strong>
          </div>

          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="border-b border-base-300">
                <th class="text-left p-2 font-semibold w-24">Datum</th>
                <th class="text-left p-2 font-semibold">Vorgang</th>
                <th class="text-left p-2 font-semibold w-56">Beleg</th>
                <th class="text-right p-2 font-semibold w-20">Menge</th>
                <th class="text-right p-2 font-semibold w-20">Bestand</th>
              </tr>
            </thead>
            <tbody>
              <tr class="border-b border-base-200 bg-base-200">
                <td class="p-2">heute</td>
                <td class="p-2">Bestand im Technikerlager</td>
                <td class="p-2"></td>
                <td class="p-2 text-right">—</td>
                <td class="p-2 text-right font-medium">{{ ledgerStart() }}</td>
              </tr>

              @for (event of active.detail?.ledger ?? []; track $index) {
                <tr class="border-b border-base-200" [class.opacity-50]="event.after_target">
                  <td class="p-2 whitespace-nowrap">{{ formatDate(event.date) }}</td>
                  <td class="p-2">
                    <div>{{ eventLabel(event) }}</div>
                    @if (event.own_operation) {
                      <span class="bm-badge bm-badge-xs bm-badge-neutral">Dieser Auftrag</span>
                    }
                    @if (event.after_target) {
                      <span class="bm-badge bm-badge-xs bm-badge-warning">nach dem Termin</span>
                    }
                    @if (arrivalSourceLabel(event); as quelle) {
                      <div class="text-xs opacity-60">{{ quelle }}</div>
                    }
                    @if (event.shipped_date) {
                      <div class="text-xs" [class.text-error]="isOverdue(event)" [class.opacity-60]="!isOverdue(event)">
                        {{ shippedDateLabel(event) }} {{ formatDate(event.shipped_date) }}
                        @if (isOverdue(event)) {
                          <span class="font-semibold">· überfällig</span>
                        }
                      </div>
                    }
                  </td>
                  <td class="p-2 text-xs">
                    @if (event.ticket_id) {
                      <a class="bm-link" [routerLink]="['/ticket/', event.ticket_id]" target="_new">
                        Ticket {{ event.ticket_id }}
                      </a>
                    }
                    @if (event.delivery_note_number || event.delivery_note_id) {
                      <div class="opacity-60">
                        LS {{ event.delivery_note_number || event.delivery_note_id }}
                      </div>
                    }
                    @if (event.pickup_operation_id) {
                      <div class="opacity-60">
                        Abholauftrag {{ event.pickup_done ? "erledigt" : "offen" }}
                      </div>
                    }
                  </td>
                  <td class="p-2 text-right whitespace-nowrap" [class.text-error]="event.change < 0">
                    {{ event.change > 0 ? "+" : "" }}{{ event.change }}
                  </td>
                  <td class="p-2 text-right font-medium">
                    {{ event.after_target ? "—" : event.balance }}
                  </td>
                </tr>
              }
            </tbody>
          </table>

          @if ((active.detail?.ledger ?? []).length === 0) {
            <div class="text-sm opacity-60 mt-3">
              Keine Zu- oder Abgänge bis zum Auftragstermin — es zählt allein der aktuelle Bestand.
            </div>
          }
        </div>
      }
    </div>
  </div>
}

<!-- Artikel-Modal (Anlegen/Bearbeiten einer Bedarfszeile). -->
@if (showModal()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (mousedown)="onSearchBlur()">
    <div class="bg-base-100 rounded-lg shadow-xl w-full max-w-3xl mx-4 p-5 flex flex-col gap-4"
         (mousedown)="$event.stopPropagation()">
      <h3 class="font-bold text-base">
        {{ editingRequirement() ? "Auftragsbedarf bearbeiten" : "Auftragsbedarf hinzufügen" }}
      </h3>

      <div class="flex gap-3 items-start">
        <div class="w-24 shrink-0">
          <label class="bm-label text-xs mb-1">Menge</label>
          <input type="number" class="bm-input bm-input-sm w-full" [(ngModel)]="formQuantity" [min]="1" />
        </div>

        <div class="flex-1 min-w-0">
          <label class="bm-label text-xs mb-1">Artikelvorlage/Freitext</label>
          <div class="relative">
            <input
              type="text"
              class="bm-input bm-input-sm w-full"
              [(ngModel)]="formSearchText"
              (input)="onSearchInput()"
              placeholder="Artikelvorlage suchen oder Freitext..."
            />
            @if (searchResults().length > 0) {
              <div class="absolute z-10 w-full mt-1 border border-base-300 rounded bg-base-100 shadow-lg max-h-40 overflow-y-auto">
                @for (result of searchResults(); track result.id) {
                  <div class="px-3 py-1.5 text-sm cursor-pointer hover:bg-base-200" (mousedown)="selectTemplate(result)">
                    {{ result.description }}
                  </div>
                }
              </div>
            }
          </div>
          @if (!selectedTemplate() && formSearchText.trim().length > 0) {
            <span class="text-xs opacity-60 mt-1 block">
              Freitext ohne Artikelvorlage — die Verfügbarkeit lässt sich dafür nicht prüfen.
            </span>
          }
        </div>
      </div>

      <div class="flex justify-end gap-2 pt-2 border-t border-base-200">
        <button class="bm-btn bm-btn-ghost bm-btn-sm" (click)="closeModal()">Abbrechen</button>
        <button class="bm-btn bm-btn-primary bm-btn-sm" [disabled]="!canSave()" (click)="saveRequirement()">
          Speichern
        </button>
      </div>
    </div>
  </div>
}
